import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "next-themes";

import "@/App.css";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import PageLoader from "@/components/PageLoader";
import OfflineBanner from "@/components/OfflineBanner";
import PwaInstallPrompt from "@/components/PwaInstallPrompt";
import { queryClient } from "@/lib/queryClient";

// Login is eager — every visit hits it, no point in lazy.
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Layout from "@/components/Layout";

const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Patients = lazy(() => import("@/pages/Patients"));
const PatientDetail = lazy(() => import("@/pages/PatientDetail"));
const Authorisations = lazy(() => import("@/pages/Authorisations"));
const TransplantLoad = lazy(() => import("@/pages/TransplantLoad"));
const MeetingPrep = lazy(() => import("@/pages/MeetingPrep"));
const Backlog = lazy(() => import("@/pages/Backlog"));
const Followups = lazy(() => import("@/pages/Followups"));
const ProgramPage = lazy(() => import("@/pages/ProgramPage"));
const Reports = lazy(() => import("@/pages/Reports"));
const AdminDoctors = lazy(() => import("@/pages/admin/Doctors"));
const Profile = lazy(() => import("@/pages/Profile"));
const ChangePassword = lazy(() => import("@/pages/ChangePassword"));
const SetupMfa = lazy(() => import("@/pages/SetupMfa"));
const AdminUsers = lazy(() => import("@/pages/AdminUsers"));
const AdminImport = lazy(() => import("@/pages/AdminImport"));
const AdminAudit = lazy(() => import("@/pages/AdminAudit"));
const AdminRecycleBin = lazy(() => import("@/pages/AdminRecycleBin"));
const AdminBackups = lazy(() => import("@/pages/AdminBackups"));
const AdminDiagnosisReview = lazy(() => import("@/pages/AdminDiagnosisReview"));
const AdminLegacyArchives = lazy(() => import("@/pages/AdminLegacyArchives"));
const AdminLists = lazy(() => import("@/pages/admin/Lists"));
const FeedbackPage = lazy(() => import("@/pages/FeedbackPage"));
const Help = lazy(() => import("@/pages/Help"));
const Tasks = lazy(() => import("@/pages/Tasks"));
const AdminTaskTemplates = lazy(() => import("@/pages/admin/TaskTemplates"));
const Todos = lazy(() => import("@/pages/Todos"));
const ClinicDaySheet = lazy(() => import("@/pages/ClinicDaySheet"));

export default function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <Toaster position="top-right" richColors closeButton />
          <OfflineBanner />
          <PwaInstallPrompt />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route
                index
                element={
                  <Suspense fallback={<PageLoader />}>
                    <Dashboard />
                  </Suspense>
                }
              />
              <Route
                path="/patients"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <Patients />
                  </Suspense>
                }
              />
              <Route
                path="/patients/:id"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <PatientDetail />
                  </Suspense>
                }
              />
              <Route
                path="/authorisations"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <Authorisations />
                  </Suspense>
                }
              />
              <Route
                path="/transplant-load"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <TransplantLoad />
                  </Suspense>
                }
              />
              <Route
                path="/meeting-prep"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <MeetingPrep />
                  </Suspense>
                }
              />
              <Route
                path="/todos"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <Todos />
                  </Suspense>
                }
              />
              <Route
                path="/clinic-day-sheet"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <ClinicDaySheet />
                  </Suspense>
                }
              />
              <Route
                path="/backlog"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <Backlog />
                  </Suspense>
                }
              />
              <Route
                path="/followups"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <Followups />
                  </Suspense>
                }
              />
              <Route
                path="/program"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <ProgramPage />
                  </Suspense>
                }
              />
              <Route
                path="/reports"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <Reports />
                  </Suspense>
                }
              />
              <Route
                path="/profile"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <Profile />
                  </Suspense>
                }
              />
              <Route
                path="/profile/change-password"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <ChangePassword />
                  </Suspense>
                }
              />
              <Route
                path="/setup-mfa"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <SetupMfa />
                  </Suspense>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <ProtectedRoute roles={["admin", "coordinator"]}>
                    <Suspense fallback={<PageLoader />}>
                      <AdminUsers />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/import"
                element={
                  <ProtectedRoute roles={["admin", "coordinator"]}>
                    <Suspense fallback={<PageLoader />}>
                      <AdminImport />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/doctors"
                element={
                  <ProtectedRoute roles={["admin", "coordinator"]}>
                    <Suspense fallback={<PageLoader />}>
                      <AdminDoctors />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/audit"
                element={
                  <ProtectedRoute roles={["admin", "coordinator"]}>
                    <Suspense fallback={<PageLoader />}>
                      <AdminAudit />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/recycle-bin"
                element={
                  <ProtectedRoute roles={["admin", "coordinator"]}>
                    <Suspense fallback={<PageLoader />}>
                      <AdminRecycleBin />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/backups"
                element={
                  <ProtectedRoute roles={["admin", "coordinator"]}>
                    <Suspense fallback={<PageLoader />}>
                      <AdminBackups />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/diagnosis-review"
                element={
                  <ProtectedRoute roles={["admin", "coordinator"]}>
                    <Suspense fallback={<PageLoader />}>
                      <AdminDiagnosisReview />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/legacy-archives"
                element={
                  <ProtectedRoute roles={["admin", "coordinator"]}>
                    <Suspense fallback={<PageLoader />}>
                      <AdminLegacyArchives />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/lists"
                element={
                  <ProtectedRoute roles={["admin", "coordinator"]}>
                    <Suspense fallback={<PageLoader />}>
                      <AdminLists />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/task-templates"
                element={
                  <ProtectedRoute roles={["admin", "coordinator"]}>
                    <Suspense fallback={<PageLoader />}>
                      <AdminTaskTemplates />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/feedback"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <FeedbackPage />
                  </Suspense>
                }
              />
              <Route
                path="/help"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <Help />
                  </Suspense>
                }
              />
              <Route
                path="/tasks"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <Tasks />
                  </Suspense>
                }
              />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
    </ThemeProvider>
  );
}
